import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { mselDoctorRoutes } from '@/modules/msel-doctor/routes'
import { metlSmithRoutes } from '@/modules/metl-smith/routes'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: (to) => {
        const authStore = useAuthStore()
        return authStore.isAuthenticated ? '/scenarios' : '/login'
      }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresGuest: true }
    },
    ...mselDoctorRoutes,
    ...metlSmithRoutes,
    {
      path: '/users',
      component: () => import('@/layouts/AppLayout.vue'),
      meta: { requiresAuth: true, requiresRole: 'admin' },
      children: [
        {
          path: '',
          name: 'users',
          component: () => import('@/views/UsersView.vue')
        }
      ]
    },
    {
      path: '/version',
      component: () => import('@/layouts/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'version',
          component: () => import('@/views/VersionInfoView.vue')
        }
      ]
    },
    {
      path: '/system',
      component: () => import('@/layouts/AppLayout.vue'),
      meta: { requiresAuth: true, requiresRole: 'admin' },
      children: [
        {
          path: '',
          name: 'system',
          component: () => import('@/views/SystemDataManagement.vue')
        }
      ]
    },
    // 404 catch-all route - must be last
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue')
    }
  ],
})

// Navigation guards
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()

  // Always check authentication first, except for login page and 404
  if (to.name !== 'login' && to.name !== 'not-found') {
    if (!authStore.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }
  }

  // Check if route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authStore.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    // Check if route requires specific role
    const requiredRole = to.matched.find(record => record.meta.requiresRole)?.meta.requiresRole
    if (requiredRole && !authStore.hasRole(requiredRole as string)) {
      next({ name: 'scenarios' })
      return
    }

    const routeModule = to.matched.find(record => record.meta.module)?.meta.module as string | undefined
    if (routeModule && !authStore.hasModuleAccess(routeModule)) {
      if (authStore.hasModuleAccess('msel-doctor')) {
        next({ name: 'scenarios' })
      } else if (authStore.hasModuleAccess('metl-smith')) {
        next({ name: 'tasks-measures' })
      } else {
        next({ name: 'version' })
      }
      return
    }
  }

  // Redirect authenticated users away from login page
  if (to.matched.some(record => record.meta.requiresGuest)) {
    if (authStore.isAuthenticated) {
      const redirect = to.query.redirect as string
      next(redirect || { name: 'scenarios' })
      return
    }
  }

  next()
})

export default router
